import { Link } from "react-router-dom";
import useSeo from "@/lib/useSeo";
import { Button } from "@/components/ui/button";
import { Sparkles, ArrowRight, Compass, Users, Wand2, Mail, MapPin, Heart } from "lucide-react";

const NAVY = "#1a2a6c";
const TEAL = "#00b09b";

export default function About() {
  useSeo({
    title: "About Hook2Income AI — Turn Your Knowledge Into a Course & Income",
    description:
      "Hook2Income AI helps everyday people turn their knowledge, skills and experience into a digital course and income stream — no tech skills needed. Meet founder Lee Osborne and see what makes us different.",
    path: "/about",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      name: "About Hook2Income AI",
      url: "https://hook2income.com/about",
      about: {
        "@type": "Organization",
        name: "Hook2Income AI",
        url: "https://hook2income.com/",
        logo: "https://hook2income.com/logo-full.png",
        email: "support@hook2income.com",
        founder: { "@type": "Person", name: "Lee Osborne" },
        description:
          "AI-guided platform that helps complete beginners turn their knowledge into a digital mini-course, content campaign and income stream.",
      },
    },
  });

  return (
    <div
      className="min-h-screen bg-[#FAFAF9] text-[#1C1917]"
      data-testid="about-page"
      style={{ fontFamily: "'Poppins', ui-sans-serif, system-ui, sans-serif" }}
    >
      {/* Header */}
      <header className="mx-auto flex max-w-3xl items-center justify-between px-5 pt-8">
        <Link to="/" className="text-lg font-extrabold tracking-tight" data-testid="about-logo">
          Hook2Income<span style={{ color: TEAL }}> AI</span>
        </Link>
        <Link to="/idea-generator" data-testid="about-header-cta">
          <Button variant="ghost" className="rounded-full text-sm font-semibold">
            Free Idea Generator
          </Button>
        </Link>
      </header>

      <main className="mx-auto max-w-3xl px-5 py-10 sm:py-14">
        {/* Hero — what it is + who it's for */}
        <section className="fade-up text-center" data-testid="about-hero">
          <div
            className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl"
            style={{ background: `${TEAL}1a`, color: TEAL }}
          >
            <Sparkles size={26} />
          </div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em]" style={{ color: TEAL }}>
            About Hook2Income
          </p>
          <h1 className="mt-3 font-head text-4xl font-bold leading-tight sm:text-5xl" style={{ color: NAVY }}>
            Turn what you know into a course — and an income
          </h1>
          <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg">
            Hook2Income is for everyday people who want to turn their knowledge, skills, or life
            experience into a digital course and a real income stream. You don't need to be a
            techie, a marketer, or a designer. If you know something worth sharing, we'll help you
            package it, promote it, and put it out into the world.
          </p>
        </section>

        {/* Founder story */}
        <section className="fade-up mt-14 rounded-3xl border border-border bg-white p-6 shadow-sm sm:p-9" data-testid="about-founder">
          <div className="mb-3 flex items-center gap-2 text-xs font-bold uppercase tracking-wide" style={{ color: TEAL }}>
            <Heart size={15} /> The story
          </div>
          <h2 className="font-head text-2xl font-bold sm:text-3xl" style={{ color: NAVY }}>
            Hi, I'm Lee Osborne
          </h2>
          <p className="mt-2 flex items-center gap-1.5 text-sm font-medium text-muted-foreground">
            <MapPin size={15} /> Surrey, United Kingdom
          </p>
          <div className="mt-5 space-y-4 text-base leading-relaxed text-[#1C1917]/90">
            <p>
              I built Hook2Income for one simple reason: I kept meeting brilliant people who had
              real knowledge to share, but felt completely stuck when it came to turning it into
              something online. They'd say things like <em>"I'm not technical"</em> or{" "}
              <em>"I wouldn't know where to start."</em>
            </p>
            <p>
              I've seen how the tools out there are usually built for people who already know
              marketing, funnels, and content. That leaves out the everyday person — the one with a
              skill, a passion, or hard-won experience who just wants a clear path forward.
            </p>
            <p>
              So I set out to build something different: a friendly, guided way for complete
              beginners to create digital income, without needing any technical skills. Hook2Income
              is the tool I wish had existed when I started — one that holds your hand, step by step,
              and does the heavy lifting with you.
            </p>
          </div>
        </section>

        {/* What makes us different */}
        <section className="fade-up mt-14" data-testid="about-different">
          <h2 className="text-center font-head text-2xl font-bold sm:text-3xl" style={{ color: NAVY }}>
            What makes Hook2Income different
          </h2>
          <div className="mt-8 grid gap-5 sm:grid-cols-3">
            <DiffCard
              icon={Wand2}
              title="AI-guided"
              body="Our AI does the heavy lifting — writing your course, hooks, and posts — so you're never staring at a blank page."
              testid="about-diff-ai"
            />
            <DiffCard
              icon={Compass}
              title="Step-by-step"
              body="A clear, guided journey walks you from your first idea to ready-to-publish content, one simple step at a time."
              testid="about-diff-steps"
            />
            <DiffCard
              icon={Users}
              title="Built for beginners"
              body="No jargon, no tech skills, no camera or editing. If you can answer a few questions, you can build with Hook2Income."
              testid="about-diff-beginners"
            />
          </div>
        </section>

        {/* Contact */}
        <section
          className="fade-up mt-14 rounded-3xl border border-border p-6 text-center sm:p-9"
          style={{ background: `${NAVY}0a` }}
          data-testid="about-contact"
        >
          <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-xl" style={{ background: `${TEAL}1a`, color: TEAL }}>
            <Mail size={20} />
          </div>
          <h2 className="font-head text-xl font-bold sm:text-2xl" style={{ color: NAVY }}>
            Got a question? We're real people.
          </h2>
          <p className="mt-2 text-base text-muted-foreground">
            We'd genuinely love to hear from you. Reach out any time:
          </p>
          <a
            href="mailto:support@hook2income.com"
            data-testid="about-contact-email"
            className="mt-3 inline-block text-lg font-bold hover:underline"
            style={{ color: NAVY }}
          >
            support@hook2income.com
          </a>
        </section>

        {/* CTA */}
        <section className="fade-up mt-14 text-center" data-testid="about-cta">
          <h2 className="font-head text-2xl font-bold sm:text-3xl" style={{ color: NAVY }}>
            Ready to find your idea?
          </h2>
          <p className="mx-auto mt-3 max-w-md text-base text-muted-foreground">
            Answer 5 simple questions and our free AI Idea Generator will find your strongest course
            idea — no account needed to start.
          </p>
          <Link to="/idea-generator" data-testid="about-cta-btn">
            <Button
              className="mt-6 rounded-full px-8 py-6 text-base font-semibold text-white hover:opacity-90"
              style={{ background: NAVY }}
            >
              Try the free Idea Generator <ArrowRight size={18} className="ml-1.5" />
            </Button>
          </Link>
          <p className="mt-3 text-xs text-muted-foreground">Free · no account needed to start</p>
        </section>

        {/* Footer */}
        <footer className="mt-16 border-t border-border pt-6 text-center text-sm text-muted-foreground" data-testid="about-footer">
          <p>
            © {new Date().getFullYear()} Hook2Income AI ·{" "}
            <a href="mailto:support@hook2income.com" className="hover:underline">
              support@hook2income.com
            </a>
          </p>
          <p className="mt-1">
            <Link to="/idea-generator" className="hover:underline">Idea Generator</Link>
            {" · "}
            <Link to="/register" className="hover:underline">Create account</Link>
            {" · "}
            <Link to="/login" className="hover:underline">Sign in</Link>
          </p>
        </footer>
      </main>
    </div>
  );
}

function DiffCard({ icon: Icon, title, body, testid }) {
  return (
    <div className="rounded-2xl border border-border bg-white p-6 shadow-sm" data-testid={testid}>
      <div
        className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl"
        style={{ background: `${TEAL}1a`, color: TEAL }}
      >
        <Icon size={20} />
      </div>
      <h3 className="font-head text-lg font-bold" style={{ color: NAVY }}>
        {title}
      </h3>
      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{body}</p>
    </div>
  );
}
